import React, { useState, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import TimelineCard from './TimelineCard';
import EntryDetail from './EntryDetail';
import TimelineFilters from './TimelineFilters';
import PhotoCompare from './PhotoCompare';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';

const DEFAULT_FILTERS = {
  sort: 'date_desc',
  glowRange: 'all',
  tags: [],
  hasPhoto: 'all',
  hasTreatment: 'all',
};

function applyFilters(entries, filters) {
  let result = [...entries];
  if (filters.glowRange === 'high') result = result.filter(e => e.glowScore >= 8);
  else if (filters.glowRange === 'mid') result = result.filter(e => e.glowScore >= 5 && e.glowScore <= 7);
  else if (filters.glowRange === 'low') result = result.filter(e => e.glowScore <= 4);
  if (filters.tags.length > 0) result = result.filter(e => filters.tags.every(t => (e.tags || []).includes(t)));
  if (filters.hasPhoto === 'yes') result = result.filter(e => e.photos?.length > 0 || !!e.imageUrl);
  if (filters.hasTreatment === 'yes') result = result.filter(e => (e.treatments || []).length > 0);
  switch (filters.sort) {
    case 'date_asc': result.sort((a, b) => new Date(a.date) - new Date(b.date)); break;
    case 'glow_desc': result.sort((a, b) => b.glowScore - a.glowScore); break;
    case 'glow_asc': result.sort((a, b) => a.glowScore - b.glowScore); break;
    case 'water_desc': result.sort((a, b) => (b.water || 0) - (a.water || 0)); break;
    default: result.sort((a, b) => new Date(b.date) - new Date(a.date));
  }
  return result;
}

export default function TimelineView({ entries }) {
  const [selected, setSelected] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const filtered = useMemo(() => applyFilters(entries, filters), [entries, filters]);
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  return (
    <div className="relative h-full flex flex-col">
      <div className="flex-1 overflow-y-auto pb-4 px-5">
        <div className="pt-8 pb-5 flex items-center gap-3">
          <img
            src="https://media-manager-c.questera.ai/greta-media/1593c15ad88356866a2eabcebc8aef2c67d9fc5afee8d56439ced245a0c6c3417efffbd95aedd1b6f68107af9f17a1fd/images/aW1hZ2UvcG5n/44d2d46ca6ce6a8943aba646038b0408.png"
            alt="Glowlytix Logo"
            className="w-10 h-10 object-contain drop-shadow-sm flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="font-sans text-xs uppercase tracking-widest font-semibold text-slate-500 mb-0.5">
              Data-driven skin tracking
            </p>
            <h1 className="font-sans text-2xl font-medium text-slate-900 tracking-tight leading-none">Your Skin Health</h1>
          </div>
          <button
            onClick={() => setShowCompare(true)}
            title="Compare photos"
            className="w-10 h-10 rounded-full bg-white/70 border border-slate-200 flex items-center justify-center shadow-sm active:bg-white transition-colors flex-shrink-0"
          >
            <SafeIcon icon={FiArrowLeft} className="text-slate-600 text-xs" />
            <SafeIcon icon={FiArrowRight} className="text-slate-600 text-xs" />
          </button>
        </div>

        <TimelineFilters filters={filters} onChange={setFilters} />

        {isFiltered && (
          <p className="font-sans text-xs text-slate-500 mb-3">
            Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of {entries.length} entries
          </p>
        )}

        <div className="space-y-4">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <span className="text-5xl mb-4">✦</span>
              {entries.length === 0 ? (
                <>
                  <p className="font-sans text-xl font-medium text-slate-800">Start your journey</p>
                  <p className="font-sans text-sm text-slate-500 mt-2">Tap + to add your first entry</p>
                </>
              ) : (
                <>
                  <p className="font-sans text-xl font-medium text-slate-800">No matches found</p>
                  <p className="font-sans text-sm text-slate-500 mt-2">Try adjusting your filters</p>
                </>
              )}
            </div>
          ) : (
            filtered.map(entry => (
              <TimelineCard key={entry.id} entry={entry} onClick={setSelected} />
            ))
          )}
        </div>
      </div>

      <AnimatePresence>
        {selected && <EntryDetail entry={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {showCompare && (
          <PhotoCompare entries={entries} onClose={() => setShowCompare(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}